import type {
  CanonicalNutritionEntry,
  CanonicalNutritionEntryDraft,
  MacroTotals,
  MealType,
} from '@calorie-tracker/nutrition';

export const dbTableNames = [
  'users',
  'sessions',
  'accounts',
  'verifications',
  'profiles',
  'daily_goals',
  'meals',
  'canonical_foods',
  'canonical_food_versions',
  'food_sources',
  'food_entries',
  'recipes',
  'recipe_versions',
  'recipe_ingredients',
  'log_chat_sessions',
  'log_chat_messages',
  'log_chat_message_attachments',
  'image_uploads',
  'ai_suggestions',
  'audit_events',
] as const;

export type DbTableName = (typeof dbTableNames)[number];

export interface TimestampFields {
  createdAt: Date;
  updatedAt: Date;
}

export const defaultDailyGoal = {
  calories: 2000,
  proteinGrams: 150,
  carbsGrams: 200,
  fatGrams: 70,
} as const;

export interface DailyGoalRecord extends TimestampFields {
  id: string;
  userId: string;
  calories: number;
  proteinGrams: number;
  carbsGrams: number;
  fatGrams: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface MealRecord extends TimestampFields {
  id: string;
  userId: string;
  mealType: MealType;
  consumedOn: string;
  consumedAt: Date;
}

export interface CanonicalFoodRecord extends TimestampFields {
  id: string;
  userId: string;
  name: string;
  normalizedName: string;
  defaultQuantity: {
    amount: number;
    unit: string;
  };
  currentVersionId: string | null;
  source: CanonicalNutritionEntry['source'];
  confidence: CanonicalNutritionEntry['confidence'];
  firstLoggedAt: Date;
  lastLoggedAt: Date;
  logCount: number;
  archivedAt: Date | null;
}

export interface CanonicalFoodVersionRecord extends TimestampFields {
  id: string;
  canonicalFoodId: string;
  userId: string;
  quantity: {
    amount: number;
    unit: string;
  };
  canonicalQuantity: {
    amount: number;
    unit: string;
  };
  macros: MacroTotals;
  source: CanonicalNutritionEntry['source'];
  confidence: CanonicalNutritionEntry['confidence'];
  foodSourceId: string | null;
  createdFromEntryId: string | null;
}

type FoodEntrySnapshot = Pick<
  CanonicalNutritionEntry,
  | 'originalInput'
  | 'normalizedName'
  | 'inputQuantity'
  | 'canonicalQuantity'
  | 'macros'
  | 'source'
  | 'confidence'
  | 'requiresReview'
  | 'consumedAt'
>;

export interface FoodEntryRecord extends TimestampFields, FoodEntrySnapshot {
  id: string;
  mealId: string;
  userId: string;
  foodSourceId: string;
  canonicalFoodId?: string | null;
  canonicalFoodVersionId?: string | null;
}

export interface RecipeRecord extends TimestampFields {
  id: string;
  userId: string;
  name: string;
  normalizedName: string;
  archivedAt: Date | null;
}

export interface RecipeVersionRecord extends TimestampFields {
  id: string;
  recipeId: string;
  userId: string;
  versionNumber: number;
  totalYield: {
    amount: number;
    unit: string;
  };
  totalMacros: MacroTotals;
}

export interface RecipeIngredientRecord extends TimestampFields {
  id: string;
  recipeVersionId: string;
  userId: string;
  position: number;
  entry: CanonicalNutritionEntryDraft;
}

export const imageUploadPurposes = ['food-label', 'meal-photo'] as const;

export type ImageUploadPurpose = (typeof imageUploadPurposes)[number];

export const imageUploadStatuses = ['uploaded', 'processed', 'failed'] as const;

export type ImageUploadStatus = (typeof imageUploadStatuses)[number];

export const aiSuggestionStatuses = ['draft', 'confirmed', 'rejected', 'superseded'] as const;

export type AiSuggestionStatus = (typeof aiSuggestionStatuses)[number];

export const logChatModes = ['food', 'recipe'] as const;

export type LogChatMode = (typeof logChatModes)[number];

export const logChatSessionStatuses = ['active', 'archived'] as const;

export type LogChatSessionStatus = (typeof logChatSessionStatuses)[number];

export const logChatMessageRoles = ['assistant', 'user'] as const;

export type LogChatMessageRole = (typeof logChatMessageRoles)[number];

export const logChatMessageStatuses = ['complete', 'error', 'streaming'] as const;

export type LogChatMessageStatus = (typeof logChatMessageStatuses)[number];

export const auditActorTypes = ['user', 'system', 'ai', 'mcp'] as const;

export type AuditActorType = (typeof auditActorTypes)[number];

export interface ImageUploadMetadata {
  [key: string]: unknown;
}

export interface ImageUploadRecord extends TimestampFields {
  id: string;
  userId: string;
  r2ObjectKey: string;
  bucketName: string;
  originalFileName: string;
  contentType: string;
  byteSize: number;
  sha256: string | null;
  purpose: ImageUploadPurpose;
  status: ImageUploadStatus;
  metadata: ImageUploadMetadata | null;
}

export interface LogChatSessionRecord extends TimestampFields {
  id: string;
  userId: string;
  title: string;
  mode: LogChatMode;
  status: LogChatSessionStatus;
  activeSuggestionId: string | null;
}

export interface LogChatMessageRecord extends TimestampFields {
  id: string;
  sessionId: string;
  userId: string;
  role: LogChatMessageRole;
  text: string;
  status: LogChatMessageStatus;
  toolActivities: Array<Record<string, unknown>> | null;
}

export interface LogChatMessageAttachmentRecord {
  id: string;
  messageId: string;
  uploadId: string;
  kind: 'image';
  name: string;
  contentType: string;
  size: number;
  createdAt: Date;
}

export interface FoodSourceEvidence {
  [key: string]: unknown;
}

export const d1BindingName = 'DB';
export const foodImagesBucketBindingName = 'FOOD_IMAGES';

export const cloudflareVariableNames = {
  appName: 'APP_NAME',
  apiBaseUrl: 'API_BASE_URL',
  betterAuthBaseUrl: 'BETTER_AUTH_BASE_URL',
  d1DatabaseName: 'D1_DATABASE_NAME',
  r2BucketName: 'R2_BUCKET_NAME',
} as const;

export const cloudflareSecretNames = {
  betterAuthSecret: 'BETTER_AUTH_SECRET',
} as const;
